/**
 * MA-ARCH-130: Profit Path Detail Page
 * 
 * Shows a single profit path with its nested funnels and campaigns.
 * Allows linking existing funnels and campaigns to the profit path.
 */

import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Route,
  DollarSign,
  Users,
  Filter,
  Megaphone,
  Plus,
  Edit,
  ExternalLink,
  TrendingUp,
  CheckCircle2,
  FlaskConical,
  Link2,
  Unlink,
  Phone,
  History,
  GitBranch,
  Globe,
  FileEdit,
  Archive,
  AlertCircle,
  Layers,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useModuleAccessLevel } from "@/hooks/usePermissions";
// MA-AN-131: Profit Path Performance (compact view)
import ProfitPathPerformance from "@/components/warroom/ProfitPathPerformance";
// MA-ARCH-178: Landing Page Performance
import LandingPagePerformance, { LandingPageVersionMetrics } from "@/components/landing-pages/LandingPagePerformance";
// MA-ARCH-181: Attribution Coverage
import AttributionCoverageSummary from "@/components/attribution/AttributionCoverageSummary";

// =============================================================================
// MA-ARCH-173: FUNNEL VERSIONS HOOKS
// =============================================================================

function useFunnelVersions(profitPathId) {
  return useQuery({
    queryKey: ["funnel-versions", profitPathId],
    queryFn: async () => {
      const res = await api.get(`/funnel-versions/profit-path/${profitPathId}/versions`);
      return res.data;
    },
    enabled: !!profitPathId,
  });
}

function useCreateDraftVersion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ profitPathId, changeSummary, changeType }) => {
      const res = await api.post(`/funnel-versions/profit-path/${profitPathId}/versions`, {
        change_summary: changeSummary,
        change_type: changeType || "mixed",
      });
      return res.data;
    },
    onSuccess: (_, { profitPathId }) => {
      queryClient.invalidateQueries({ queryKey: ["funnel-versions", profitPathId] });
      toast.success("Draft version created");
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || "Failed to create draft version");
    },
  });
}

function usePublishVersion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ versionId, profitPathId }) => {
      const res = await api.post(`/funnel-versions/${versionId}/publish`, {});
      return res.data;
    },
    onSuccess: (_, { profitPathId }) => {
      queryClient.invalidateQueries({ queryKey: ["funnel-versions", profitPathId] });
      queryClient.invalidateQueries({ queryKey: ["profit-path", profitPathId] });
      toast.success("Version published");
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || "Failed to publish version");
    },
  });
}

// =============================================================================
// API HOOKS
// =============================================================================

function useProfitPath(id) {
  return useQuery({
    queryKey: ["profit-path", id],
    queryFn: async () => {
      const res = await api.get(`/profit-paths/${id}`);
      return res.data;
    },
    enabled: !!id,
  });
}

function useAvailableFunnels(profitPathId) {
  return useQuery({
    queryKey: ["available-funnels", profitPathId],
    queryFn: async () => {
      // Get all funnels without a profit_path_id or with this profit_path_id
      const res = await api.get("/funnels?limit=100");
      return res.data.items?.filter(
        (f) => !f.profit_path_id || f.profit_path_id === profitPathId
      ) || [];
    },
    enabled: !!profitPathId,
  });
}

function useAvailableCampaigns(profitPathId) {
  return useQuery({
    queryKey: ["available-campaigns", profitPathId],
    queryFn: async () => {
      // Get all campaigns without a profit_path_id or with this profit_path_id
      const res = await api.get("/marketing-campaigns?limit=100");
      return res.data.items?.filter(
        (c) => !c.profit_path_id || c.profit_path_id === profitPathId
      ) || [];
    },
    enabled: !!profitPathId,
  });
}

function useLinkFunnel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ profitPathId, funnelId }) => {
      const res = await api.post(`/profit-paths/${profitPathId}/funnels/${funnelId}`);
      return res.data;
    },
    onSuccess: (_, { profitPathId }) => {
      queryClient.invalidateQueries({ queryKey: ["profit-path", profitPathId] });
      queryClient.invalidateQueries({ queryKey: ["available-funnels"] });
      toast.success("Funnel linked to profit path");
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || "Failed to link funnel");
    },
  });
}

function useLinkCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ profitPathId, campaignId }) => {
      const res = await api.post(`/profit-paths/${profitPathId}/campaigns/${campaignId}`);
      return res.data;
    },
    onSuccess: (_, { profitPathId }) => {
      queryClient.invalidateQueries({ queryKey: ["profit-path", profitPathId] });
      queryClient.invalidateQueries({ queryKey: ["available-campaigns"] });
      toast.success("Campaign linked to profit path");
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || "Failed to link campaign");
    },
  });
}

// =============================================================================
// HELPERS
// =============================================================================

function formatCurrency(value) {
  if (value === null || value === undefined) return "$0";
  if (Math.abs(value) >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (Math.abs(value) >= 1000) return `$${(value / 1000).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
}

const STATUS_COLORS = {
  draft: "bg-slate-500/20 text-slate-300 border-slate-500/30",
  active: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  paused: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  archived: "bg-red-500/20 text-red-300 border-red-500/30",
};

// =============================================================================
// LINK DIALOG
// =============================================================================

function LinkDialog({ open, onOpenChange, type, items, onLink, loading }) {
  const [selectedId, setSelectedId] = useState("");

  const handleLink = () => {
    if (selectedId) {
      onLink(selectedId);
      setSelectedId("");
    }
  };

  const availableItems = items?.filter((i) => !i.profit_path_id) || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass border-white/10">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-purple-400" />
            Link {type === "funnel" ? "Funnel" : "Campaign"}
          </DialogTitle>
          <DialogDescription>
            Select a {type} to link to this profit path
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Select value={selectedId} onValueChange={setSelectedId}>
            <SelectTrigger className="bg-white/5 border-white/10">
              <SelectValue placeholder={`Select a ${type}...`} />
            </SelectTrigger>
            <SelectContent className="glass border-white/10 max-h-60">
              {availableItems.length === 0 ? (
                <div className="p-4 text-center text-slate-400 text-sm">
                  No unlinked {type}s available
                </div>
              ) : (
                availableItems.map((item) => (
                  <SelectItem key={item.id} value={item.id}>
                    {item.name}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleLink} disabled={!selectedId || loading}>
            {loading ? "Linking..." : "Link"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// MA-ARCH-176: OPTIMIZATION HISTORY COMPONENT
// =============================================================================

function OptimizationHistory({ profitPathId }) {
  const { data, isLoading } = useQuery({
    queryKey: ["optimization-history", profitPathId],
    queryFn: async () => {
      const res = await api.get(`/recommendation-analytics/profit-path/${profitPathId}`);
      return res.data;
    },
    enabled: !!profitPathId,
  });

  if (isLoading) {
    return (
      <Card className="glass border-white/10">
        <CardContent className="py-12 text-center">
          <p className="text-slate-500 animate-pulse">Loading optimization history...</p>
        </CardContent>
      </Card>
    );
  }

  const outcomes = data?.outcomes || [];
  const summary = data?.impact_summary || {};

  return (
    <Card className="glass border-white/10">
      <CardHeader>
        <CardTitle className="text-lg text-indigo-200">Optimization History</CardTitle>
        <CardDescription>
          Applied recommendations and their performance impact
        </CardDescription>
        {outcomes.length > 0 && (
          <div className="flex items-center gap-4 mt-2 text-xs" data-testid="optimization-summary">
            {summary.positive > 0 && (
              <span className="flex items-center gap-1 text-emerald-400">
                <TrendingUp className="h-3 w-3" />
                {summary.positive} positive
              </span>
            )}
            {summary.negative > 0 && (
              <span className="flex items-center gap-1 text-red-400">
                <TrendingUp className="h-3 w-3 rotate-180" />
                {summary.negative} negative
              </span>
            )}
            {summary.neutral > 0 && (
              <span className="flex items-center gap-1 text-slate-400">
                {summary.neutral} neutral
              </span>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent>
        {outcomes.length === 0 ? (
          <div className="py-12 text-center" data-testid="no-optimization-history">
            <TrendingUp className="h-10 w-10 text-slate-600 mx-auto mb-2" />
            <p className="text-slate-400">No optimization history yet</p>
            <p className="text-xs text-slate-500 mt-1">
              Applied FunnelAssist recommendations will appear here with their performance impact
            </p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="optimization-outcomes-list">
            {outcomes.map((o) => (
              <OptimizationOutcomeRow key={o.recommendation_id} outcome={o} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function OptimizationOutcomeRow({ outcome }) {
  const impactColors = {
    positive: { bg: "bg-emerald-500/10", ring: "ring-emerald-500/20", text: "text-emerald-400" },
    negative: { bg: "bg-red-500/10", ring: "ring-red-500/20", text: "text-red-400" },
    neutral: { bg: "bg-white/5", ring: "ring-white/10", text: "text-slate-400" },
  };
  const stateCfg = {
    low_data: { label: "Low Data", color: "text-slate-400" },
    early_signal: { label: "Early", color: "text-amber-400" },
    significant: { label: "Stable", color: "text-green-400" },
  };

  const ic = impactColors[outcome.impact] || impactColors.neutral;
  const sc = stateCfg[outcome.analytical_state] || stateCfg.low_data;
  const metrics = outcome.new_version_metrics || {};
  const deltas = outcome.deltas || {};

  return (
    <div
      className={`p-3 rounded-lg ${ic.bg} ring-1 ${ic.ring}`}
      data-testid={`outcome-row-${outcome.recommendation_id}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-medium ${ic.text}`}>
            {outcome.recommendation_type === "strategy_change" ? (
              <GitBranch className="h-4 w-4 inline mr-1" />
            ) : (
              <FlaskConical className="h-4 w-4 inline mr-1" />
            )}
            {outcome.recommendation_type === "strategy_change" ? "Strategy Change" : "Execution"}
          </span>
          <Badge variant="outline" className={`text-[10px] ${sc.color} border-white/10`}>
            {sc.label}
          </Badge>
        </div>
        <span className="text-xs text-slate-500">
          {outcome.applied_at ? new Date(outcome.applied_at).toLocaleDateString() : ""}
          {outcome.days_since_apply > 0 && ` (${outcome.days_since_apply}d ago)`}
        </span>
      </div>

      {/* Metrics row */}
      <div className="grid grid-cols-4 gap-2 mt-2 text-xs">
        <div className="text-center">
          <div className="text-slate-500">Leads</div>
          <div className="text-white font-medium">{metrics.leads ?? 0}</div>
        </div>
        <div className="text-center">
          <div className="text-slate-500">Bookings</div>
          <div className="text-white font-medium">{metrics.appointments ?? 0}</div>
        </div>
        <div className="text-center">
          <div className="text-slate-500">Clients</div>
          <div className="text-white font-medium">{metrics.clients ?? 0}</div>
        </div>
        <div className="text-center">
          <div className="text-slate-500">Revenue</div>
          <div className="text-white font-medium">${(metrics.revenue ?? 0).toLocaleString()}</div>
        </div>
      </div>

      {/* Deltas */}
      {Object.keys(deltas).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {Object.entries(deltas).map(([key, val]) => {
            if (val == null) return null;
            const isPos = val > 0;
            const label = key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
            return (
              <span
                key={key}
                className={`text-[10px] px-1.5 py-0.5 rounded ${
                  isPos ? "bg-emerald-500/10 text-emerald-400" :
                  val < 0 ? "bg-red-500/10 text-red-400" :
                  "bg-slate-500/10 text-slate-400"
                }`}
              >
                {label}: {isPos ? "+" : ""}{(val * 100).toFixed(1)}%
              </span>
            );
          })}
        </div>
      )}

      {/* Version reference */}
      <div className="flex items-center gap-2 mt-2 text-[10px] text-slate-500">
        {outcome.applied_funnel_version_id && (
          <span>Version: {outcome.applied_funnel_version_id}</span>
        )}
        {outcome.baseline_funnel_version_id && (
          <span>vs Baseline: {outcome.baseline_funnel_version_id}</span>
        )}
      </div>
    </div>
  );
}


// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function ProfitPathDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [linkFunnelOpen, setLinkFunnelOpen] = useState(false);
  const [linkCampaignOpen, setLinkCampaignOpen] = useState(false);
  
  // MA-RBAC-BATCH3-INFRA-115A: Check profit_paths access level for builder actions
  const { hasFullAccess: canEditProfitPaths } = useModuleAccessLevel("profit_paths");

  const pathQuery = useProfitPath(id);
  const funnelsQuery = useAvailableFunnels(id);
  const campaignsQuery = useAvailableCampaigns(id);
  const linkFunnelMutation = useLinkFunnel();
  const linkCampaignMutation = useLinkCampaign();
  
  // MA-ARCH-173: Funnel Versions
  const versionsQuery = useFunnelVersions(id);
  const createDraftMutation = useCreateDraftVersion();
  const publishVersionMutation = usePublishVersion();

  const path = pathQuery.data;

  if (pathQuery.isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse text-slate-400">Loading profit path...</div>
      </div>
    );
  }

  if (!path) {
    return (
      <div className="p-6">
        <div className="text-red-400">Profit path not found</div>
        <Button variant="outline" onClick={() => navigate("/app/profit-paths")} className="mt-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Profit Paths
        </Button>
      </div>
    );
  }

  const handleLinkFunnel = (funnelId) => {
    linkFunnelMutation.mutate({ profitPathId: id, funnelId });
    setLinkFunnelOpen(false);
  };

  const handleLinkCampaign = (campaignId) => {
    linkCampaignMutation.mutate({ profitPathId: id, campaignId });
    setLinkCampaignOpen(false);
  };

  return (
    <div className="space-y-6 p-6" data-testid="profit-path-detail">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/app/profit-paths")}
            className="mb-2 -ml-2 text-slate-400 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <div className="flex items-center gap-3">
            <Route className="h-8 w-8 text-blue-400" />
            <div>
              <h1 className="text-2xl font-bold text-white">{path.name}</h1>
              {path.description && (
                <p className="text-slate-400 mt-1">{path.description}</p>
              )}
            </div>
            <Badge variant="outline" className={STATUS_COLORS[path.status] || STATUS_COLORS.draft}>
              {path.status}
            </Badge>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* MA-RBAC-BATCH3-INFRA-115A: Hide Edit button for read-only users */}
          {canEditProfitPaths && (
            <Button variant="outline" onClick={() => navigate(`/app/profit-paths?edit=${id}`)}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          )}
          {/* MA-RBAC-BATCH3-INFRA-115A: Hide Duplicate button for read-only users */}
          {canEditProfitPaths && (
            <Button
              variant="outline"
              className="text-indigo-300 border-indigo-500/30 hover:bg-indigo-500/10"
              onClick={() => navigate(`/app/profit-paths?duplicate=${id}`)}
              data-testid="duplicate-as-strategy-btn"
            >
              <GitBranch className="h-4 w-4 mr-2" />
              Duplicate as New Strategy
            </Button>
          )}
        </div>
      </div>

      {/* Message Strategy Card */}
      <Card className="glass border-blue-500/20 bg-blue-500/5">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2 text-blue-300">
            <FlaskConical className="h-4 w-4 text-blue-400" />
            Message Strategy
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-slate-400 uppercase">Category</p>
              <p className="text-white font-medium capitalize">
                {path.message_category?.replace(/_/g, " ") || "—"}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-400 uppercase">Variant</p>
              <p className="text-white font-medium">{path.message_variant || "—"}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 uppercase">Vertical</p>
              <p className="text-white font-medium capitalize">{path.vertical || "—"}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400 uppercase">Revenue</p>
              <p className="text-emerald-400 font-bold text-lg">
                {formatCurrency(path.total_revenue)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="glass border-white/10">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-400 uppercase">Funnels</p>
              <p className="text-2xl font-bold text-white">{path.funnel_count}</p>
            </div>
            <Filter className="h-8 w-8 text-slate-500" />
          </CardContent>
        </Card>
        <Card className="glass border-white/10">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-400 uppercase">Campaigns</p>
              <p className="text-2xl font-bold text-white">{path.campaign_count}</p>
            </div>
            <Megaphone className="h-8 w-8 text-slate-500" />
          </CardContent>
        </Card>
        <Card className="glass border-white/10">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-400 uppercase">Leads</p>
              <p className="text-2xl font-bold text-white">{path.lead_count}</p>
            </div>
            <Users className="h-8 w-8 text-blue-400/50" />
          </CardContent>
        </Card>
        <Card className="glass border-white/10">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-400 uppercase">Clients</p>
              <p className="text-2xl font-bold text-white">{path.client_count}</p>
            </div>
            <CheckCircle2 className="h-8 w-8 text-emerald-400/50" />
          </CardContent>
        </Card>
      </div>

      {/* MA-AN-131: Performance Analytics Summary (Compact View) */}
      <Card className="glass border-white/10">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2 text-emerald-300">
            <TrendingUp className="h-4 w-4 text-emerald-400" />
            Performance Analytics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ProfitPathPerformance 
            period="mtd"
            compact={true}
          />
          {/* MA-ARCH-181: Attribution Coverage for this Profit Path */}
          <div className="mt-4 pt-3 border-t border-white/8" data-testid="pp-attribution-coverage">
            <AttributionCoverageSummary profitPathId={id} />
          </div>
        </CardContent>
      </Card>

      {/* Funnels, Campaigns & Scripts Tabs */}
      <Tabs defaultValue="funnels" className="space-y-4">
        <TabsList className="inline-flex h-auto w-fit max-w-full flex-wrap justify-start gap-1 bg-indigo-500/20 text-indigo-100 ring-1 ring-indigo-400/20">
          <TabsTrigger value="funnels" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-100">
            <Filter className="h-4 w-4 mr-2" />
            Funnels ({path.funnels?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="campaigns" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-100">
            <Megaphone className="h-4 w-4 mr-2" />
            Campaigns ({path.campaigns?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="scripts" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-100">
            <Phone className="h-4 w-4 mr-2" />
            Scripts ({path.default_script_id ? 1 : 0})
          </TabsTrigger>
          <TabsTrigger value="versions" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-100" data-testid="versions-tab">
            <History className="h-4 w-4 mr-2" />
            Versions ({versionsQuery.data?.total || 0})
          </TabsTrigger>
          <TabsTrigger value="optimization" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-100" data-testid="optimization-tab">
            <TrendingUp className="h-4 w-4 mr-2" />
            Optimization
          </TabsTrigger>
          <TabsTrigger value="landing_page" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-100" data-testid="landing-page-tab">
            <Layers className="h-4 w-4 mr-2" />
            Landing Page
          </TabsTrigger>
        </TabsList>

        {/* Funnels Tab */}
        <TabsContent value="funnels">
          <Card className="glass border-white/10">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg text-indigo-200">Funnels</CardTitle>
                <CardDescription>
                  Funnels linked to this profit path
                </CardDescription>
              </div>
              <Button size="sm" onClick={() => setLinkFunnelOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Link Funnel
              </Button>
            </CardHeader>
            <CardContent>
              {!path.funnels?.length ? (
                <div className="py-8 text-center">
                  <Filter className="h-10 w-10 text-slate-600 mx-auto mb-2" />
                  <p className="text-slate-400">No funnels linked yet</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setLinkFunnelOpen(true)}
                    className="mt-3"
                  >
                    Link a Funnel
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {path.funnels.map((funnel) => (
                    <div
                      key={funnel.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Filter className="h-5 w-5 text-indigo-400" />
                        <div>
                          <p className="font-medium text-white">{funnel.name}</p>
                          <p className="text-xs text-slate-400">
                            {funnel.is_active ? "Active" : "Inactive"}
                            {funnel.slug && ` • /${funnel.slug}`}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/app/funnels/${funnel.id}`)}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Campaigns Tab */}
        <TabsContent value="campaigns">
          <Card className="glass border-white/10">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg text-indigo-200">Campaigns</CardTitle>
                <CardDescription>
                  Traffic sources (Meta, Google, etc.) driving this profit path
                </CardDescription>
              </div>
              <Button size="sm" onClick={() => setLinkCampaignOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Link Campaign
              </Button>
            </CardHeader>
            <CardContent>
              {!path.campaigns?.length ? (
                <div className="py-8 text-center">
                  <Megaphone className="h-10 w-10 text-slate-600 mx-auto mb-2" />
                  <p className="text-slate-400">No campaigns linked yet</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setLinkCampaignOpen(true)}
                    className="mt-3"
                  >
                    Link a Campaign
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {path.campaigns.map((campaign) => (
                    <div
                      key={campaign.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Megaphone className="h-5 w-5 text-blue-400" />
                        <div>
                          <p className="font-medium text-white">{campaign.name}</p>
                          <p className="text-xs text-slate-400">
                            {campaign.source_platform || "—"} • {campaign.status}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/app/campaigns?id=${campaign.id}`)}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Scripts Tab */}
        <TabsContent value="scripts">
          <Card className="glass border-white/10">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg text-indigo-200">Scripts</CardTitle>
                <CardDescription>
                  Call scripts linked to this profit path
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              {!path.default_script_id ? (
                <div className="py-8 text-center">
                  <Phone className="h-10 w-10 text-slate-600 mx-auto mb-2" />
                  <p className="text-slate-400">No default script set</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/app/profit-paths/${id}/edit`)}
                    className="mt-3"
                  >
                    Set Default Script
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div
                    className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors cursor-pointer"
                    onClick={() => navigate(`/app/scripts/${path.default_script_id}/play`)}
                  >
                    <div className="flex items-center gap-3">
                      <Phone className="h-5 w-5 text-indigo-400" />
                      <div>
                        <p className="font-medium text-white">Default Script</p>
                        <p className="text-xs text-slate-400">
                          ID: {path.default_script_id}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/app/scripts/${path.default_script_id}/play`);
                      }}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* MA-ARCH-173: Versions Tab */}
        <TabsContent value="versions">
          <Card className="glass border-white/10">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg text-indigo-200">Funnel Version History</CardTitle>
                <CardDescription>
                  Execution versions — strategy stays stable, execution evolves
                </CardDescription>
              </div>
              <Button
                size="sm"
                onClick={() => createDraftMutation.mutate({ profitPathId: id, changeSummary: "New iteration" })}
                disabled={createDraftMutation.isPending}
                data-testid="create-draft-version-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Draft
              </Button>
            </CardHeader>
            <CardContent>
              {versionsQuery.isLoading ? (
                <div className="py-8 text-center text-slate-500 animate-pulse">Loading versions...</div>
              ) : !versionsQuery.data?.versions?.length ? (
                <div className="py-8 text-center">
                  <History className="h-10 w-10 text-slate-600 mx-auto mb-2" />
                  <p className="text-slate-400">No versions yet</p>
                  <p className="text-xs text-slate-500 mt-1">Publish a funnel to create the first version</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {versionsQuery.data.versions.map((v) => (
                    <div
                      key={v.id}
                      className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                        v.is_active
                          ? "bg-emerald-500/10 ring-1 ring-emerald-500/20"
                          : v.status === "draft"
                          ? "bg-amber-500/5 ring-1 ring-amber-500/15"
                          : "bg-white/5"
                      }`}
                      data-testid={`version-row-${v.version_number}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`flex h-8 w-8 items-center justify-center rounded-lg text-xs font-bold ${
                          v.is_active
                            ? "bg-emerald-500/20 text-emerald-300"
                            : v.status === "draft"
                            ? "bg-amber-500/20 text-amber-300"
                            : "bg-white/10 text-slate-400"
                        }`}>
                          v{v.version_number}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-white text-sm truncate">
                              {v.version_label || v.change_summary || `Version ${v.version_number}`}
                            </p>
                            {v.is_active && (
                              <Badge className="bg-emerald-500/20 text-emerald-300 text-[10px] px-1.5">Active</Badge>
                            )}
                            {v.status === "draft" && (
                              <Badge variant="outline" className="text-amber-300 border-amber-500/30 text-[10px] px-1.5">Draft</Badge>
                            )}
                            {v.status === "archived" && (
                              <Badge variant="outline" className="text-slate-500 border-slate-600 text-[10px] px-1.5">
                                <Archive className="h-2.5 w-2.5 mr-1" />
                                Archived
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-slate-400 mt-0.5">
                            {v.change_type && (
                              <span className="capitalize">{v.change_type}</span>
                            )}
                            {v.published_at && (
                              <span>Published {new Date(v.published_at).toLocaleDateString()}</span>
                            )}
                            {!v.published_at && v.created_at && (
                              <span>Created {new Date(v.created_at).toLocaleDateString()}</span>
                            )}
                            {v.has_leads && (
                              <span className="text-blue-400">{v.lead_count} lead{v.lead_count !== 1 ? "s" : ""}</span>
                            )}
                            {v.snapshot_metadata && (
                              <span>{v.snapshot_metadata.node_count} nodes</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {v.status === "draft" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => publishVersionMutation.mutate({ versionId: v.id, profitPathId: id })}
                            disabled={publishVersionMutation.isPending}
                            data-testid={`publish-version-${v.version_number}-btn`}
                          >
                            <Globe className="h-3 w-3 mr-1" />
                            Publish
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* MA-ARCH-176: Optimization History Tab */}
        <TabsContent value="optimization">
          <OptimizationHistory profitPathId={id} />
        </TabsContent>

        {/* MA-ARCH-178: Landing Page Performance Tab */}
        <TabsContent value="landing_page">
          <Card className="glass border-white/10">
            <CardHeader>
              <CardTitle className="text-lg text-indigo-200">Landing Page Performance</CardTitle>
              <CardDescription>
                First-party metrics for the active landing page version
              </CardDescription>
            </CardHeader>
            <CardContent>
              <LandingPagePerformance
                versionId={path.active_landing_page_version_id}
                profitPathId={id}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      <LinkDialog
        open={linkFunnelOpen}
        onOpenChange={setLinkFunnelOpen}
        type="funnel"
        items={funnelsQuery.data}
        onLink={handleLinkFunnel}
        loading={linkFunnelMutation.isPending}
      />
      <LinkDialog
        open={linkCampaignOpen}
        onOpenChange={setLinkCampaignOpen}
        type="campaign"
        items={campaignsQuery.data}
        onLink={handleLinkCampaign}
        loading={linkCampaignMutation.isPending}
      />
    </div>
  );
}
